/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { AlertTriangle, IndianRupee, ScrollText, Wallet } from 'lucide-react';
import { type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { QueryState } from '../../components/ui/query-state.js';
import { StatCard } from '../../components/ui/stat-card.js';
import { type FinanceFigmaStats } from '../../lib/api/finance-dashboard.js';
import { useFinanceDashboard } from '../../lib/queries/finance-dashboard.js';
import { formatInr } from '../../lib/utils/money.js';

import { FinanceProformaTable } from './finance-proforma-table.js';

function collectedPct(figma: FinanceFigmaStats): string {
  if (figma.invoicedMonthPaise <= 0) {
    return '0';
  }
  return ((figma.collectedMonthPaise / figma.invoicedMonthPaise) * 100).toFixed(1);
}

/** The four Figma headline tiles, from the server-computed read-model. */
function StatsRow({ figma }: { readonly figma: FinanceFigmaStats }): JSX.Element {
  const { t } = useTranslation();
  const pending = figma.proformasDraft + figma.proformasSent;
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <StatCard
        icon={IndianRupee}
        label={t('finance.dashboard.invoiced')}
        value={formatInr(figma.invoicedMonthPaise)}
        accent="bg-[var(--accent)]"
      />
      <StatCard
        icon={Wallet}
        label={t('finance.dashboard.collected')}
        value={formatInr(figma.collectedMonthPaise)}
        accent="bg-emerald-600"
        hint={t('finance.dashboard.collectedPct', { pct: collectedPct(figma) })}
      />
      <StatCard
        icon={AlertTriangle}
        label={t('finance.dashboard.overdue')}
        value={formatInr(figma.overduePaise)}
        accent="bg-rose-500"
        hint={t('finance.dashboard.overdueCount', { count: figma.overdueCount })}
      />
      <StatCard
        icon={ScrollText}
        label={t('finance.dashboard.proformasPending')}
        value={String(pending)}
        accent="bg-amber-500"
        hint={t('finance.dashboard.proformasPendingSub')}
      />
    </div>
  );
}

/** Finance → Dashboard (Figma). Server-computed tiles + the proforma pipeline. */
export function FinanceDashboardPage(): JSX.Element {
  const { t } = useTranslation();
  const dashboard = useFinanceDashboard();

  return (
    <div className="p-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white">
            {t('finance.dashboard.title')}
          </h1>
          <p className="text-sm text-slate-400">{t('finance.dashboard.subtitle')}</p>
        </div>
        <Link
          to="/finance/proformas"
          className="focus-ring inline-flex items-center gap-2 rounded-xl bg-[var(--accent)] px-4 py-2.5 text-sm font-semibold text-white hover:opacity-90"
        >
          <ScrollText className="h-4 w-4" aria-hidden="true" />
          {t('finance.dashboard.generateCycle')}
        </Link>
      </div>

      <QueryState
        isLoading={dashboard.isLoading}
        isError={dashboard.isError}
        isEmpty={false}
        loadingLabel={t('common.loading')}
        errorLabel={t('common.loadError')}
        emptyLabel=""
      >
        {dashboard.data !== undefined && (
          <div className="space-y-6">
            <StatsRow figma={dashboard.data.figma} />
            <FinanceProformaTable />
          </div>
        )}
      </QueryState>
    </div>
  );
}
